import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import cookies from 'browser-cookies';
import { ScreenGroups, Screens } from '@src/containers/QuizContainer/types';
import { getScreensFromScreenGroup } from '@src/containers/QuizContainer/screenHelpers';
import { hasQuizRecommendations } from 'src/utilities/userUtility';
import { OptimizelyDecision, useDecision } from '@optimizely/react-sdk';
import { QUIZ_SCREEN_ORDER } from '@src/constants/featureFlags';
import { QuizReducerInterface, quizInitialState, quizReducer } from 'src/reducers/quizReducer';
import { employerIds } from '@src/constants/employerResources';
import { useRecommendations } from '../RecommendationsProvider';
import { updateScreenMap } from './helpers';

export const QuizDispatchContext = createContext(null);
export const QuizContext = createContext<QuizReducerInterface | undefined>(undefined);

export const useQuizDispatch = () => {
	const context = useContext(QuizDispatchContext);

	if (context === undefined) {
		throw new Error('useQuizDispatch must be used within QuizProvider.');
	}

	return context;
};

export const useQuiz = () => {
	const context = useContext(QuizContext);

	if (context === undefined) {
		throw new Error('useQuiz must be used within QuizProvider.');
	}

	return context;
};

interface Decision extends OptimizelyDecision {
	variables: {
		'screen-order': {
			order: ScreenGroups[];
		};
	};
}

export const QuizProvider: React.FC = ({ children }) => {
	const [quizState, dispatch] = useReducer(quizReducer, quizInitialState);
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { recommendationsData } = useRecommendations();
	const [decision] = useDecision(QUIZ_SCREEN_ORDER);

	const hasCompletedQuiz = hasQuizRecommendations(recommendationsData);
	const hasSeenQuiz = cookies.get('hasSeenQuiz');

	useEffect(() => {
		let screenGroupOrder: ScreenGroups[] = quizState.screenGroupOrder;
		let screenMapping = quizState.screenMapping;

		if (decision.enabled) {
			screenGroupOrder = (decision.variables as Decision['variables'])['screen-order'].order;
			screenMapping = updateScreenMap(screenGroupOrder);
		}

		let welcomeSequence = [Screens.OPTIN];
		if (hasSeenQuiz || hasCompletedQuiz) {
			welcomeSequence = [Screens.OPTIN, Screens.WELCOME_1];
			const firstScreenOfFirstScreenGroup = getScreensFromScreenGroup(
				screenGroupOrder[0],
				screenMapping
			)[0];

			if ([employerIds.walmart, employerIds.disney].includes(authUser.employer_uuid)) {
				welcomeSequence.push(Screens.WELCOME_2, Screens[firstScreenOfFirstScreenGroup]);
			} else {
				welcomeSequence.push(Screens[firstScreenOfFirstScreenGroup]);
			}
		} else {
			cookies.set('hasSeenQuiz', 'true');
		}

		dispatch({
			type: 'set_initial_quiz_state',
			screenGroupOrder,
			screenHistory: welcomeSequence,
			screenMapping,
			welcomeSequence,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [decision, hasCompletedQuiz, hasSeenQuiz, authUser.employer_uuid]);

	return (
		<QuizDispatchContext.Provider value={{ dispatch }}>
			<QuizContext.Provider value={quizState}>{children}</QuizContext.Provider>
		</QuizDispatchContext.Provider>
	);
};
